import { Flex, Text, Stack, HStack, Avatar, Button, IconButton, Spinner, useToast, VStack, Link, Tooltip, MenuButton, Menu, MenuItem, MenuList, MenuDivider, Badge } from '@chakra-ui/react'
import { IComment } from '@/shared/IComment'
import { AutoResizeTextarea } from '@/app/components/AutoResizeTextarea'
import { formatDateFromNow, stringifyError } from '@/shared/parse'
import React from 'react'
import { FiActivity, FiCheck, FiEdit3, FiMoreVertical, FiRepeat, FiX } from 'react-icons/fi'
import { AuthService } from '@/app/services/AuthService'
import { DeleteCommentDialog } from './DeleteCommentDialog'
import { CommentsService } from '@/app/services/CommentsService'
import { COMMENT_STATUS } from '@/shared/constants'
import { Model } from 'survey-core'

export const Comment = (props: {
  comment: IComment,
  survey: Model,
  onSend: (comment: IComment) => Promise<any>,
  onDelete: (comment: IComment) => Promise<any>,
  onEdit: (comment: IComment) => Promise<any>,
  onCancel: () => void,
  onNavigate: (comment: IComment) => void,
  onReply: (comment: IComment) => void,
  editCommentId: string | undefined,
  status?: string,
  showReply?: boolean,
}) => {
  const [comment, setComment] = React.useState(props.comment);
  const [isExpanded, setIsExpanded] = React.useState(false);

  React.useEffect(() => {
    setComment(props.comment);
  }, [props.comment, props.editCommentId]);

  const toast = useToast();

  const [isSending, setIsSending] = React.useState(false);
  const onSend = async () => {
    if (props.onSend) {
      setIsSending(true);
      try {
        await props.onSend(comment);
      } catch (err) {
        toast({
          title: 'Error sending comment',
          description: stringifyError(err),
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      } finally {
        setIsSending(false);
      }
    }
  }

  const account = AuthService.account;
  const isEditVisible = account?.username === comment.email || AuthService.isAdmin;
  const isDeleteVisible = account?.username === comment.email || AuthService.isAdmin;
  const canResolve = AuthService.isStaff;

  const question = CommentsService.getCommentQuestionTag(comment, props.survey);

  const edit = props.editCommentId === comment.id;

  const [isUpdating, setIsUpdating] = React.useState(false);

  const updateStatus = async (status: COMMENT_STATUS) => {
    try {
      setIsUpdating(true);
      const updated = { ...comment, status }
      await props.onSend(updated);
      setComment(updated);
    } catch (err: any) {
      toast({
        title: stringifyError(err),
        description: stringifyError(err.internalError),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }finally {
      setIsUpdating(false);
    }
  }

  const [isDeleteCommentDialogOpen, setIsDeleteCommentDialogOpen] = React.useState(false);

  const badgeColorScheme = CommentsService.getCommentStatusColor(comment.status);
  const badgeText = CommentsService.getCommentStatusText(comment.status);

  return (
    <Stack w='full' py='2' key={comment.id} spacing="2">
      <Stack direction="row" justify="space-between" spacing="2">
        <HStack minW='30%' spacing="2">
          <Avatar name={comment.username} size='xs' opacity='75%' />
          <VStack align='start' gap='0'>
            <Text color="fg.emphasized">{comment.username}</Text>
            <Text fontSize='smaller' color="fg.subtle">{comment.role}</Text>
          </VStack>
        </HStack>
        <HStack>
          {isUpdating && <Spinner size='xs' />}
          {!!comment.status && <Badge size='xs' colorScheme={badgeColorScheme}>{badgeText}</Badge>}
          {!comment.threadId &&
            <Tooltip label={`Navigate to ${comment.page} ${comment.question ?? ''}`} aria-label="A tooltip">
              <Link fontSize='small' fontWeight='bold' onClick={() => props.onNavigate(comment)} >{question}</Link>
            </Tooltip>}
          <Menu>
            <MenuButton as={IconButton} variant='ghost' icon={<FiMoreVertical />} cursor='pointer' size='sm' />
            <MenuList>
              {!comment.threadId && <>
                {!comment.status && <MenuItem icon={<FiActivity />} onClick={() => updateStatus(COMMENT_STATUS.ACTION_REQUIRED)}>Action Required</MenuItem>}
                {(!comment.status || comment.status === COMMENT_STATUS.ACTION_REQUIRED || comment.status === COMMENT_STATUS.REOPENED)
                  && <MenuItem
                    icon={<FiCheck />}
                    isDisabled={!canResolve}
                    title={canResolve ? undefined : "Only staff members can resolve comments"}
                    onClick={() => updateStatus(COMMENT_STATUS.RESOLVED)}
                  >Resolve</MenuItem>}
                {comment.status === COMMENT_STATUS.RESOLVED && <MenuItem icon={<FiRepeat />} onClick={() => updateStatus(COMMENT_STATUS.REOPENED)}>Reopen</MenuItem>}
                {<MenuDivider />}
              </>}
              <MenuItem icon={<FiEdit3 />} disabled={!isEditVisible} onClick={() => props.onEdit(comment)}>Edit Comment</MenuItem>
              <MenuItem icon={<FiX />} disabled={!isDeleteVisible} onClick={() => setIsDeleteCommentDialogOpen(true)}>Delete Comment</MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Stack>
      <VStack spacing="2" align='end'>
        {edit
          ? <AutoResizeTextarea
            rows={3}
            fontSize='normal'
            onClick={(val) => setIsExpanded(!val)}
            autoFocus
            w='full'
            placeholder={comment.threadId ? 'Enter your response' : 'Enter your comment here'}
            value={comment.message}
            onChange={e => setComment(c => ({ ...c, message: e.target.value }))}
          />
          : <Text
            w='full'
            fontSize='normal'
            onClick={() => setIsExpanded(val => !val)}
            sx={{ '-webkit-box-orient': 'vertical', '-webkit-line-clamp': isExpanded ? '' : '4', overflow: 'hidden', display: '-webkit-box' }}>{comment.message}</Text>
        }
        <HStack w='full' justify='space-between'>
          <Flex fontSize='small' color="fg.subtle">{formatDateFromNow(comment.timestamp)}</Flex>
          <HStack minH='8' spacing="2">
            {edit && <Button isDisabled={isSending || !comment.message} size='xs' onClick={onSend}><HStack spacing="1">{isSending && <Spinner size='xs' />}<Text>Save</Text></HStack></Button>}
            {edit && <Button size='xs' variant="outline" onClick={props.onCancel}>Cancel</Button>}
            {!edit && props.showReply && <Button size='xs' onClick={() => props.onReply(comment)}><Text>Reply</Text></Button>}
          </HStack>
        </HStack>
      </VStack>
      {isDeleteCommentDialogOpen && <DeleteCommentDialog comment={comment} onClose={() => setIsDeleteCommentDialogOpen(false)} onDelete={props.onDelete} />}
    </Stack>
  )
}